import { makeStyles } from '@material-ui/core/styles';

const BLUE = '#0B64D1';
const VIOLET = '#6C4BF4';
const GRADIENT = `linear-gradient(135deg, ${BLUE} 0%, ${VIOLET} 100%)`;

/**
 * Bottom-left, clear of the "last updated" pill on the right. Under the app's
 * dialogs (MUI uses 1300) so booking dialogs still open on top of the chat.
 */
const Z = 1250;

export const useStyles = makeStyles((theme) => ({
  dock: {
    position: 'fixed' as const,
    left: '24px',
    bottom: '24px',
    zIndex: Z,
    display: 'flex',
    alignItems: 'flex-end',
    gap: '10px',
    [theme.breakpoints.down(600)]: {
      left: '16px',
      bottom: '16px',
    },
  },
  launcher: {
    position: 'relative' as const,
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    border: 'none',
    padding: 0,
    background: GRADIENT,
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 22px rgba(76, 70, 230, 0.38)',
    animation: '$float 3.2s ease-in-out infinite',
    transition: 'box-shadow 0.2s ease',
    '&::before': {
      // Soft halo that breathes behind the robot.
      content: '""',
      position: 'absolute' as const,
      inset: '-6px',
      borderRadius: '50%',
      background: GRADIENT,
      opacity: 0.35,
      zIndex: -1,
      animation: '$halo 2.8s ease-out infinite',
    },
    '&:hover': {
      boxShadow: '0 10px 28px rgba(76, 70, 230, 0.5)',
    },
    '&:hover $launcherFace': {
      transform: 'rotate(-10deg) scale(1.08)',
    },
    '&:focus-visible': { outline: '3px solid #B9A8FF', outlineOffset: '3px' },
  },
  launcherFace: {
    display: 'flex',
    transition: 'transform 0.25s cubic-bezier(.34,1.56,.64,1)',
  },
  greeting: {
    marginBottom: '14px',
    padding: '9px 14px',
    borderRadius: '14px 14px 14px 4px',
    background: '#fff',
    color: '#1a1a1a',
    fontSize: '13px',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 6px 20px rgba(20, 30, 60, 0.16)',
    whiteSpace: 'nowrap' as const,
    animation: '$popIn 0.35s cubic-bezier(.34,1.56,.64,1) both',
    '&:focus-visible': { outline: `2px solid ${VIOLET}` },
    [theme.breakpoints.down(600)]: { display: 'none' },
  },
  panel: {
    position: 'fixed' as const,
    left: '24px',
    bottom: '24px',
    zIndex: Z,
    width: '390px',
    height: 'min(620px, calc(100vh - 48px))',
    display: 'flex',
    flexDirection: 'column' as const,
    background: '#fff',
    borderRadius: '20px',
    boxShadow: '0 18px 50px rgba(20, 30, 80, 0.25)',
    overflow: 'hidden',
    transformOrigin: 'bottom left',
    animation: '$openPanel 0.32s cubic-bezier(.2,.9,.3,1.15) both',
    [theme.breakpoints.down(600)]: {
      left: 0,
      bottom: 0,
      width: '100%',
      height: '100%',
      borderRadius: 0,
    },
  },
  header: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 10px 14px 14px',
    background: GRADIENT,
    backgroundSize: '200% 200%',
    animation: '$shimmer 8s ease infinite',
    color: '#fff',
  },
  avatar: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.18)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#fff',
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  title: {
    margin: 0,
    fontSize: '15px',
    fontWeight: 700,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  aiBadge: {
    padding: '1px 7px',
    borderRadius: '8px',
    fontSize: '10px',
    fontWeight: 800,
    letterSpacing: '0.6px',
    background: 'rgba(255, 255, 255, 0.22)',
    border: '1px solid rgba(255, 255, 255, 0.4)',
  },
  subtitle: {
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    margin: '2px 0 0',
    fontSize: '12px',
    opacity: 0.9,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  onlineDot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    background: '#5CF2A6',
    boxShadow: '0 0 0 0 rgba(92, 242, 166, 0.7)',
    animation: '$ping 2s infinite',
  },
  headerButton: {
    width: '34px',
    height: '34px',
    borderRadius: '10px',
    border: 'none',
    background: 'transparent',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s ease',
    '&:hover': { background: 'rgba(255, 255, 255, 0.18)' },
    '&:focus-visible': { outline: '2px solid #fff' },
  },
  log: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '16px 14px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
    background: 'linear-gradient(180deg, #F6F7FF 0%, #F7F9FC 100%)',
  },
  row: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
    animation: '$rise 0.28s ease-out both',
  },
  rowMine: {
    justifyContent: 'flex-end',
  },
  miniAvatar: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: GRADIENT,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  bubble: {
    maxWidth: '80%',
    padding: '10px 13px',
    borderRadius: '16px',
    fontSize: '14px',
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
  },
  fromAssistant: {
    background: '#fff',
    color: '#1a1a1a',
    border: '1px solid #E4E6F5',
    borderBottomLeftRadius: '4px',
    boxShadow: '0 1px 3px rgba(20, 30, 80, 0.05)',
  },
  fromMe: {
    background: GRADIENT,
    color: '#fff',
    borderBottomRightRadius: '4px',
  },
  failed: {
    background: '#FDECEC',
    color: '#8A1C1C',
    border: '1px solid #F5C2C2',
  },
  caret: {
    display: 'inline-block',
    width: '2px',
    height: '1em',
    marginLeft: '1px',
    verticalAlign: 'text-bottom',
    background: VIOLET,
    animation: '$caret 0.8s steps(1) infinite',
  },
  suggestions: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
    paddingLeft: '36px',
  },
  suggestion: {
    padding: '7px 12px',
    borderRadius: '16px',
    border: '1px solid #CFC6FF',
    background: '#fff',
    color: '#4B33C9',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'transform 0.15s ease, background 0.15s ease',
    animation: '$rise 0.35s ease-out both',
    '&:nth-child(2)': { animationDelay: '0.08s' },
    '&:nth-child(3)': { animationDelay: '0.16s' },
    '&:hover': { background: '#F3F0FF', transform: 'translateY(-1px)' },
    '&:focus-visible': { outline: `2px solid ${VIOLET}`, outlineOffset: '1px' },
  },
  moreChip: {
    padding: '7px 12px',
    borderRadius: '16px',
    border: '1px dashed #B9A8FF',
    background: 'transparent',
    color: '#6C4BF4',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    '&:hover': { background: '#F3F0FF' },
    '&:focus-visible': { outline: `2px solid ${VIOLET}`, outlineOffset: '1px' },
  },
  adminGroup: {
    marginLeft: '36px',
    padding: '10px 10px 8px',
    borderRadius: '14px',
    background: 'linear-gradient(135deg, #FFF8E6 0%, #FFF1D6 100%)',
    border: '1px solid #F2D49B',
    animation: '$rise 0.35s ease-out both',
  },
  adminLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    margin: '0 0 8px 2px',
    fontSize: '10.5px',
    fontWeight: 800,
    letterSpacing: '0.6px',
    textTransform: 'uppercase' as const,
    color: '#8A5A00',
  },
  adminChips: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
  },
  adminChip: {
    padding: '7px 12px',
    borderRadius: '16px',
    border: '1px solid #E0B252',
    background: '#fff',
    color: '#7A4E00',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'transform 0.15s ease, background 0.15s ease',
    '&:hover': { background: '#FFF4DD', transform: 'translateY(-1px)' },
    '&:focus-visible': { outline: '2px solid #C98A12', outlineOffset: '1px' },
  },
  qrCard: {
    marginTop: '8px',
    marginLeft: '36px',
    alignSelf: 'flex-start',
    padding: '12px 12px 8px',
    borderRadius: '16px',
    background: '#fff',
    border: '1px solid #E4E6F5',
    boxShadow: '0 4px 14px rgba(20, 30, 80, 0.08)',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '6px',
    cursor: 'zoom-in',
    animation: '$rise 0.3s ease-out both',
    '&:focus-visible': { outline: `2px solid ${VIOLET}`, outlineOffset: '2px' },
  },
  qrCaption: {
    margin: 0,
    fontSize: '12px',
    fontWeight: 600,
    color: '#4B5563',
    textAlign: 'center' as const,
  },
  qrOverlay: {
    position: 'fixed' as const,
    inset: 0,
    zIndex: 1400,
    background: 'rgba(10, 16, 40, 0.82)',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    padding: '24px',
    animation: '$openPanel 0.25s ease-out both',
  },
  qrBig: {
    background: '#fff',
    borderRadius: '20px',
    padding: '18px',
    lineHeight: 0,
  },
  qrBigCaption: {
    color: '#fff',
    fontSize: '16px',
    fontWeight: 600,
    margin: 0,
    textAlign: 'center' as const,
  },
  qrClose: {
    padding: '10px 22px',
    borderRadius: '22px',
    border: 'none',
    background: '#fff',
    color: '#1a1a1a',
    fontWeight: 700,
    fontSize: '14px',
    cursor: 'pointer',
  },
  langToggle: {
    display: 'inline-flex',
    padding: '2px',
    borderRadius: '10px',
    background: 'rgba(255, 255, 255, 0.18)',
    marginRight: '2px',
  },
  langOption: {
    border: 'none',
    background: 'transparent',
    color: 'rgba(255, 255, 255, 0.85)',
    fontSize: '11px',
    fontWeight: 800,
    letterSpacing: '0.5px',
    padding: '5px 7px',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background 0.15s ease, color 0.15s ease',
    '&:hover': { color: '#fff' },
    '&:focus-visible': { outline: '2px solid #fff' },
  },
  langOptionActive: {
    background: '#fff',
    color: '#4B33C9',
    '&:hover': { color: '#4B33C9' },
  },
  typing: {
    display: 'flex',
    gap: '4px',
    padding: '13px 14px',
    background: '#fff',
    border: '1px solid #E4E6F5',
    borderRadius: '16px',
    borderBottomLeftRadius: '4px',
  },
  dot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    background: GRADIENT,
    animation: '$bounce 1.1s infinite ease-in-out',
    '&:nth-child(2)': { animationDelay: '0.15s' },
    '&:nth-child(3)': { animationDelay: '0.3s' },
  },
  composer: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
    padding: '10px 12px 12px',
    borderTop: '1px solid #ECEEF6',
    background: '#fff',
  },
  input: {
    flex: 1,
    resize: 'none' as const,
    border: '1px solid #D5D9EA',
    borderRadius: '14px',
    padding: '10px 12px',
    fontSize: '14px',
    fontFamily: 'inherit',
    lineHeight: 1.4,
    maxHeight: '96px',
    outline: 'none',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    '&:focus': { borderColor: VIOLET, boxShadow: '0 0 0 3px rgba(108, 75, 244, 0.15)' },
  },
  send: {
    width: '42px',
    height: '42px',
    flexShrink: 0,
    borderRadius: '50%',
    border: 'none',
    background: GRADIENT,
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.15s ease, opacity 0.15s ease',
    '&:hover:not(:disabled)': { transform: 'scale(1.06)' },
    '&:disabled': { opacity: 0.4, cursor: 'default' },
    '&:focus-visible': { outline: '3px solid #B9A8FF', outlineOffset: '2px' },
  },
  footnote: {
    margin: 0,
    padding: '0 12px 8px',
    fontSize: '10.5px',
    color: '#8A93A6',
    textAlign: 'center' as const,
    background: '#fff',
  },
  srOnly: {
    position: 'absolute' as const,
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap' as const,
  },
  '@keyframes float': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(-5px)' },
  },
  '@keyframes halo': {
    '0%': { transform: 'scale(0.9)', opacity: 0.45 },
    '100%': { transform: 'scale(1.35)', opacity: 0 },
  },
  '@keyframes popIn': {
    '0%': { transform: 'scale(0.6) translateY(8px)', opacity: 0 },
    '100%': { transform: 'scale(1) translateY(0)', opacity: 1 },
  },
  '@keyframes openPanel': {
    '0%': { transform: 'scale(0.85) translateY(16px)', opacity: 0 },
    '100%': { transform: 'scale(1) translateY(0)', opacity: 1 },
  },
  '@keyframes shimmer': {
    '0%, 100%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
  },
  '@keyframes ping': {
    '0%': { boxShadow: '0 0 0 0 rgba(92, 242, 166, 0.7)' },
    '70%': { boxShadow: '0 0 0 6px rgba(92, 242, 166, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(92, 242, 166, 0)' },
  },
  '@keyframes rise': {
    '0%': { transform: 'translateY(6px)', opacity: 0 },
    '100%': { transform: 'translateY(0)', opacity: 1 },
  },
  '@keyframes bounce': {
    '0%, 80%, 100%': { transform: 'translateY(0)', opacity: 0.5 },
    '40%': { transform: 'translateY(-5px)', opacity: 1 },
  },
  '@keyframes caret': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0 },
  },
  '@media (prefers-reduced-motion: reduce)': {
    launcher: { animation: 'none', '&::before': { animation: 'none', opacity: 0 } },
    greeting: { animation: 'none' },
    panel: { animation: 'none' },
    header: { animation: 'none' },
    onlineDot: { animation: 'none' },
    row: { animation: 'none' },
    suggestion: { animation: 'none' },
    dot: { animation: 'none' },
    caret: { animation: 'none' },
    adminGroup: { animation: 'none' },
    qrCard: { animation: 'none' },
    qrOverlay: { animation: 'none' },
  },
}));
